import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ButtonSize,
  Modal,
  Button,
  ButtonVariant,
  Checkbox,
  IconButton,
  IconButtonVariant,
  Show,
  Input,
  InputSize
} from "shared/components";
import { ApplicationFeeStatus } from "shared/types";
import { Card } from "shared/components/Card";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { ApplicantStatus, ApplicantType, Application } from "shared/types";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { cx, formatPhone } from "shared/utils";
import { Table } from "shared/app/Table";
import moment from "moment";
import {
  CellContext,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Loader } from "shared/components/Loader";
import { toast } from "react-toastify";
import { ApplicationStatusBadge } from "../ApplicationStatusBadge";
import { Template } from "types/landlord/verificationTemplate";
import { Money } from "types/landlord/reports";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicationUIStatus } from "utils/get-application-ui-status";
import { getApplicantRole } from "utils/getApplicantRole";
import { Applicant } from "types/application";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import type { IconSvgElement } from "@hugeicons/react";

type ApplicationWithInvoice = Application & {
  refundable_invoice: {
    created_at: string;
    due_date?: string;
    id: number;
    paid_at?: string;
    period?: string;
    status: "fulfilled" | string;
    total: Money;
    updated_at?: string;
  };
};

type ApplicantWithInvoiceAndRefundInput = Applicant & {
  refundable_invoice: {
    created_at: string;
    due_date?: string;
    id: number;
    paid_at?: string;
    period?: string;
    status: "fulfilled" | string;
    total: Money;
    updated_at?: string;
  };
  refundAmountInput: number;
};

const getApplication = async (id: string) => {
  return instance(`/portal/v1/applications/${id}`).then(
    ({ data }) =>
      data as ApplicationWithInvoice & { verification_template: Template }
  );
};

const getApplicantDisplayData = (applicant?: {
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  email?: string | null;
  phone?: string | null;
}) => {
  if (!applicant) return " - ";
  else if (
    "first_name" in applicant &&
    applicant.first_name &&
    applicant.last_name
  )
    return getFullName({
      firstName: applicant.first_name,
      lastName: applicant.last_name,
      middleName: applicant.middle_name || "",
      short: true
    });
  else if (applicant.email) return applicant.email;
  else if (applicant.phone) return formatPhone(applicant.phone);
  else return " - ";
};

enum View {
  Cancel = "Cancel",
  ConfirmRefund = "ConfirmRefund"
}

interface RefundAmount {
  applicant_id: string | number;
  refund_amount: Money;
}

export const CancelOrRefundModal = ({
  onClose,
  applicationId,
  onSuccess,
  applicantId,
  applicants
}: {
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicantId?: string;
  applicants?: Applicant[];
}) => {
  const navigate = useNavigate();
  const [refund, setRefund] = useState(false);
  const [view, setView] = useState<View>(View.Cancel);

  const applicationMeta = useQuery({
    queryKey: ["application", String(applicationId)],
    queryFn: () => getApplication(applicationId),
    enabled: Boolean(applicationId)
  });

  const displayStatus = getApplicationUIStatus(applicationMeta.data);

  const isRefundApplicationDisabled = useMemo(() => {
    if (!applicants) return false;
    return !applicants.some(
      (applicant: Applicant) =>
        applicant.application_fee_status === ApplicationFeeStatus.Paid &&
        applicant.application_status === ApplicantStatus.Submitted
    );
  }, [applicants]);

  const allUsers = useMemo(
    () => [
      ...(applicationMeta.data?.applicants || []),
      ...(applicationMeta.data?.pending_magic_links || [])
    ],
    [
      applicationMeta.data?.applicants,
      applicationMeta.data?.pending_magic_links
    ]
  );

  const applicant = useMemo(
    () =>
      allUsers.find(
        (applicant) =>
          applicantId && String(applicant.id) === String(applicantId)
      ),
    [allUsers, applicantId]
  );

  const hasSubmittedApplications = applicationMeta.data?.applicants.some(
    (applicant) => applicant.application_status === ApplicantStatus.Submitted
  );

  const inputRefs = useRef<Record<string, HTMLInputElement>>({});

  const [refundAmounts, setRefundAmounts] = useState<Record<string, number>>(
    {}
  );

  const baseTableData = useMemo(() => {
    const applicants = applicant ? [applicant] : allUsers;

    return applicants.map((applicant) => ({
      ...applicant,
      paidBy:
        (applicant.role === ApplicantType.CoSigner &&
          applicationMeta.data?.verification_template.co_signer_fee.cents ===
            0) ||
        (applicant.role === ApplicantType.Applicant &&
          applicationMeta.data?.verification_template.applicant_fee.cents === 0)
          ? "Partner"
          : getApplicantRole(applicant.role)
    }));
  }, [applicationMeta.data?.applicants, applicantId, applicant]);

  useEffect(() => {
    const applicants = applicant ? [applicant] : allUsers;
    const initialRefundAmounts = applicants.reduce(
      (acc, applicant) => {
        const refundableAmount =
          (applicant as any).refundable_invoice?.total?.cents || 0;
        acc[String(applicant.id)] = refundableAmount;
        return acc;
      },
      {} as Record<string, number>
    );
    setRefundAmounts(initialRefundAmounts);
  }, [baseTableData]);

  const tableData = useMemo(() => {
    return baseTableData
      .filter((td) => "first_name" in td || "email" in td || "phone" in td)
      .map((applicant) => ({
        ...applicant,
        refundAmountInput: refundAmounts[String(applicant.id)]
      }));
  }, [baseTableData, refundAmounts]);

  const handleAmountChange = useCallback(
    (applicantId: string, value: string) => {
      const dollarValue = parseFloat(value) || 0;
      const centsValue = Math.round(dollarValue * 100);
      setRefundAmounts((prev) => ({
        ...prev,
        [applicantId]: centsValue
      }));
    },
    []
  );

  const setInputRef = useCallback(
    (applicantId: string) => (el: HTMLInputElement | null) => {
      if (el) inputRefs.current[applicantId] = el;
    },
    []
  );

  const canRefund = useMemo(() => {
    return tableData.every((applicant: any) => {
      if (
        ["unpaid", "refunded", "no application fee"].includes(
          applicant.application_fee_status
        )
      )
        return true;
      const applicantId = String(applicant.id);
      const amountCents = refundAmounts[applicantId] || 0;
      const maxAmountCents = applicant.refundable_invoice?.total?.cents || 0;
      return amountCents > 0 && amountCents <= maxAmountCents;
    });
  }, [tableData, refundAmounts]);

  const handleCancelOrRefund = useCallback(() => {
    if (applicantId) {
      const refund_amount = {
        cents: refundAmounts[applicantId],
        currency: "USD"
      };
      mutate({ refund_amount, refund });
    } else {
      const applicant_refunds = Object.entries(refundAmounts).map(
        ([applicantId, amount]) => ({
          applicant_id: applicantId,
          refund_amount: {
            cents: amount,
            currency: "USD"
          }
        })
      );
      mutate({ applicant_refunds, refund });
    }
  }, [applicantId, refund, refundAmounts]);

  const { mutate, isPending } = useMutation({
    mutationFn: (refundData: {
      applicant_refunds?: RefundAmount[];
      refund_amount?: Money;
      refund: boolean;
    }) => {
      if (!applicantId && applicationId)
        return instance.post(
          `/portal/v1/applications/${applicationId}/cancel`,
          refundData
        );

      if (!("active" in (applicant || {})))
        return instance.post(
          `/portal/v1/applications/${applicationId}/applicants/${applicantId}/cancel`,
          refundData
        );

      return instance.post(`/portal/v1/magic_links/${applicant?.id}/cancel`);
    },
    mutationKey: ["cancel", "application", String(applicationId)],
    onSuccess: () => {
      toast.success(`Application successfully canceled`);
      if (onSuccess) onSuccess();
      else navigate(`/applications/${applicationId}`);

      onClose();
    },
    onError: (error) => {
      toast.error(
        `Error ${refund ? "refunding" : "cancelling"} applications: ${getErrorMessage(error)}`
      );
    }
  });

  const columns = useMemo(() => {
    return [
      {
        accessorFn: (data) => getApplicantDisplayData(data),
        header: "Applicant",
        cell: (data) => (
          <p className="text-sm truncate text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.created_at",
        header: "Charge date",
        size: 120,
        cell: (data) => (
          <p className="text-sm text-content-400">
            {data.getValue() && moment(data.getValue()).isValid()
              ? moment(data.getValue()).format("MMM DD, YYYY")
              : "--"}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.total",
        header: "Amount",
        size: 120,
        cell: (
          data: CellContext<
            ApplicantWithInvoiceAndRefundInput,
            Money | undefined
          >
        ) => {
          const applicant = data.row.original;
          const applicantId = String(applicant.id);

          const value = data.getValue();
          if (!value) return <p className="text-sm text-content-400">--</p>;

          const maxAmountCents = value.cents || 0;
          const refundAmountInput = applicant.refundAmountInput || 0;

          let error = "";
          if (!refundAmountInput || refundAmountInput <= 0)
            error = "This field is required";
          else if (refundAmountInput > maxAmountCents) error = "Invalid amount";

          return (
            <Input
              id={`refund-amount-${applicantId}`}
              type="money"
              className={error ? "!mb-0.5" : "!mb-0"}
              defaultValue={String(maxAmountCents / 100)}
              ref={setInputRef(applicantId)}
              onChange={(e) => handleAmountChange(applicantId, e.target.value)}
              placeholder="0.00"
              hideOptional
              noIntlNormalization
              error={error}
              HugeErrorIcon={InformationCircleIcon as IconSvgElement}
              size={InputSize.SM}
            />
          );
        }
      }
    ];
  }, []);

  const table = useReactTable<ApplicantWithInvoiceAndRefundInput>({
    data: tableData as unknown as ApplicantWithInvoiceAndRefundInput[],
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={onClose}
      panelClassName="max-w-[600px] max-h-[640px] w-full min-h-[300px] flex flex-col h-full"
    >
      <div className="p-6 border-b border-alpha-100 flex items-center h-20 justify-between">
        <h1 className="text-xl text-content-1000 font-bold ">
          {view === View.Cancel
            ? "Confirm application cancellation"
            : "Confirm refund"}
        </h1>
        <IconButton
          onClick={onClose}
          className="ml-auto"
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </div>
      <Show if={view === View.ConfirmRefund}>
        <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
          <InfoIcon className="text-warning-500 h-5 w-5" />
          <p className="text-sm text-warning-700 font-medium">
            You cannot undo this action. You can only refund a customer one
            time, partially or fully. Once a refund is issued it can take up to
            5-7 business days.
          </p>
        </div>
      </Show>
      <div className="h-full grow-1 overflow-y-auto px-6 py-4 bg-background-900">
        {applicationMeta.isLoading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-content-500 mb-2.5">
              {view === View.Cancel
                ? "The following application will be canceled"
                : "The following items will be refunded"}
            </p>
            {view === View.Cancel ? (
              <Card
                className={cx("px-6 ", tableData.length > 1 ? "py-4" : "py-0")}
              >
                {tableData.length > 1 ? (
                  <div className="flex gap-2 items-center justify-between">
                    <div className="max-w-[380px]">
                      <p className="text-sm text-content-400 mb-2">
                        Application group
                      </p>
                      <h3 className="truncate text-base text-content-1000 font-bold pb-4 border-b border-alpha-40">
                        {tableData
                          .map((applicant) =>
                            getApplicantDisplayData(applicant)
                          )
                          .join(", ")}
                      </h3>
                    </div>

                    <ApplicationStatusBadge
                      className="shrink-0"
                      type="application"
                      status={displayStatus}
                    />
                  </div>
                ) : null}

                <ul className="mt-4">
                  {tableData.map((applicant) => (
                    <li key={applicant.email} className="mb-4">
                      <p className="text-sm text-content-1000 font-medium mb-2">
                        {getApplicantDisplayData(applicant)}
                      </p>
                      <p className="text-xs text-content-400">
                        {getApplicantRole(applicant.role)}
                      </p>
                    </li>
                  ))}
                </ul>

                {hasSubmittedApplications &&
                  applicant?.application_fee_status ===
                    ApplicationFeeStatus.Paid &&
                  !isRefundApplicationDisabled && (
                    <div className="pt-4 pb-4 border-t border-alpha-100">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={refund}
                          onChange={(e) => setRefund(e.target.checked)}
                          label="Request refund"
                        />
                      </div>
                    </div>
                  )}
              </Card>
            ) : (
              <Table table={table} rowHeight="68px" />
            )}
          </>
        )}
      </div>
      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button
          size={ButtonSize.SM}
          onClick={() => {
            if (view === View.ConfirmRefund) setView(View.Cancel);
            else onClose();
          }}
        >
          Back
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => {
            if (refund && view === View.Cancel) setView(View.ConfirmRefund);
            else if (!isPending) handleCancelOrRefund();
          }}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={
            applicationMeta.isLoading ||
            isPending ||
            (refund && view === View.ConfirmRefund && !canRefund)
          }
        >
          {refund && view === View.Cancel
            ? "Refund and cancel"
            : "Cancel application"}
        </Button>
      </div>
    </Modal>
  );
};
